/**
 * Meeting Assistant service ("Catch me up").
 * - Captures local speech via the Web Speech API (Chrome) and streams final
 *   recognized segments to the meeting server as 'transcript-segment' events.
 * - Sends 'assistant-query' questions and receives 'assistant-answer' replies.
 */
class MeetingAssistantService {
    constructor($rootScope, logService, socketService) {
        this.$rootScope = $rootScope;
        this.logger = logService.createLoggerWithSource('meetingAssistantService');
        this.socketService = socketService;

        this.recognition = null;
        this.isCapturing = false;
        this.isWaitingForAnswer = false;

        /** @type {{question: String, answer: String, source: String, timestamp: Number}[]} */
        this.answers = [];

        this.$rootScope.$on('assistant-answer', this.onAssistantAnswer.bind(this));
    }

    isSpeechCaptureSupported() {
        return typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
    }

    getIsCapturing() {
        return this.isCapturing;
    }

    getIsWaitingForAnswer() {
        return this.isWaitingForAnswer;
    }

    getAnswers() {
        return this.answers;
    }

    toggleCapture() {
        if (this.isCapturing) {
            this.stopCapture();
        } else {
            this.startCapture();
        }
    }

    startCapture() {
        if (this.isCapturing || !this.isSpeechCaptureSupported()) {
            return;
        }

        const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
        this.recognition = new SpeechRecognitionCtor();
        this.recognition.continuous = true;
        this.recognition.interimResults = false;
        this.recognition.lang = navigator.language || 'en-US';

        this.recognition.onresult = event => {
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    const text = event.results[i][0].transcript.trim();
                    if (text) {
                        this.sendTranscriptSegment(text);
                    }
                }
            }
        };

        this.recognition.onerror = event => {
            this.logger.warn('Speech recognition error', event.error);
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                this.stopCapture();
                this.$rootScope.$applyAsync();
            }
        };

        this.recognition.onend = () => {
            // Chrome stops recognition periodically; restart while capture is on.
            if (this.isCapturing && this.recognition) {
                try {
                    this.recognition.start();
                } catch (e) {
                    this.logger.warn('Unable to restart speech recognition', e);
                }
            }
        };

        try {
            this.recognition.start();
            this.isCapturing = true;
            this.logger.debug('Speech capture started');
        } catch (e) {
            this.logger.error('Unable to start speech recognition', e);
            this.recognition = null;
        }
    }

    stopCapture() {
        this.isCapturing = false;
        if (this.recognition) {
            const recognition = this.recognition;
            this.recognition = null;
            try {
                recognition.stop();
            } catch (e) {
                this.logger.warn('Unable to stop speech recognition', e);
            }
        }
        this.logger.debug('Speech capture stopped');
    }

    sendTranscriptSegment(text) {
        this.socketService.send({
            message: 'transcript-segment',
            data: { text: text }
        });
    }

    /**
     * @param question {String} empty string requests a generic "catch me up" summary
     */
    askQuestion(question) {
        this.isWaitingForAnswer = true;
        this.socketService.send({
            message: 'assistant-query',
            data: { question: question || '' }
        });
    }

    catchMeUp() {
        this.askQuestion('Summarize the meeting discussion so far, including key points and action items.');
    }

    /**
     * @param e
     * @param data {{question: String, answer: String, source: String}}
     */
    onAssistantAnswer(e, data) {
        this.isWaitingForAnswer = false;
        if (!data) {
            return;
        }
        this.answers.push({
            question: data.question,
            answer: data.answer,
            source: data.source,
            timestamp: Date.now()
        });
        this.$rootScope.$applyAsync();
    }
}

firebird.factory('meetingAssistantService', [
    '$rootScope',
    'logService',
    'socketService',
    (...deps) => new MeetingAssistantService(...deps)
]);
